import { ArrowLeft, Linkedin } from "lucide-react";
import RichText from "../components/content/RichText";
import CtaPanel from "../components/sections/CtaPanel";
import PersonCard, { PersonPhoto } from "../components/team/PersonCard";
import { moreTeam, teamProfile } from "../components/team/profile";
import Accent from "../components/ui/Accent";
import ExternalLink from "../components/ui/ExternalLink";
import TextLink from "../components/ui/TextLink";
import { routes } from "../data/site";
import { localHref } from "../lib/links";

/** "Jordan Hillstrom" → "Jordan *Hillstrom*": the family name in Golden Sun. */
function accentedName(fullName) {
  const parts = fullName.split(" ");
  if (parts.length < 2) return fullName;
  return `${parts.slice(0, -1).join(" ")} *${parts.at(-1)}*`;
}

const prose =
  "space-y-4 [&_a]:font-bold [&_a]:text-blue-deep [&_a]:underline [&_p]:mb-4 [&_p:last-child]:mb-0";

function ProfileHero({ profile }) {
  const { fullName, title, group, linkedin } = profile;
  return (
    <section className="blueprint overflow-hidden text-white">
      <div className="mx-auto grid max-w-280 items-center gap-10 px-4 pt-10 pb-12 sm:px-6 md:pt-14 md:pb-16 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-16">
        <div className="relative w-44 justify-self-start sm:w-52 lg:order-2 lg:w-full">
          <div
            aria-hidden="true"
            className="absolute inset-0 translate-x-3 translate-y-3 rounded-brand bg-yellow"
          />
          <div className="relative aspect-[4/5] overflow-hidden rounded-brand border border-white/15 shadow-lift">
            <PersonPhoto
              person={profile}
              alt={`Portrait of ${fullName}`}
              sizes="(max-width: 1024px) 208px, 340px"
              eager
            />
          </div>
        </div>
        <div className="min-w-0 lg:order-1">
          <a
            href={localHref(routes.about) + "#team"}
            className="tag inline-flex items-center gap-2 text-blue-soft transition-colors hover:text-yellow"
          >
            <ArrowLeft size={14} aria-hidden="true" />
            The team{group ? ` · ${group}` : ""}
          </a>
          <h1 className="font-display mt-4 text-[44px] uppercase sm:text-[56px] md:text-[72px]">
            <Accent>{accentedName(fullName)}</Accent>
          </h1>
          {title && (
            <p className="font-display mt-3 flex items-center gap-3 text-[18px] font-medium tracking-[0.12em] text-white/85 uppercase md:text-[21px]">
              <span aria-hidden="true" className="h-[3px] w-7 bg-yellow" />
              {title}
            </p>
          )}
          {linkedin && (
            <ExternalLink
              href={linkedin}
              className="mt-8 inline-flex items-center gap-2.5 rounded-brand border-2 border-white/35 px-5 py-2.5 text-[15px] font-bold text-white transition hover:border-yellow hover:text-yellow"
            >
              <Linkedin size={18} aria-hidden="true" />
              Connect on LinkedIn
              <span className="sr-only"> (opens in a new tab)</span>
            </ExternalLink>
          )}
        </div>
      </div>
    </section>
  );
}

function SectionBody({ section, className }) {
  return section.html ? (
    <RichText html={section.html} className={className} />
  ) : (
    <p className={className}>{section.text}</p>
  );
}

function Bio({ profile }) {
  const { story, facts, name } = profile;
  if (!story.length && !facts.length)
    return (
      <p className="text-lg text-muted">
        {name}&rsquo;s full profile is on its way.
      </p>
    );
  return (
    <div
      className={
        facts.length
          ? "grid grid-cols-1 items-start gap-12 lg:grid-cols-[minmax(0,1fr)_340px] lg:gap-16"
          : "max-w-[760px]"
      }
    >
      <div className="min-w-0 space-y-11">
        {story.map((section, i) => (
          <div key={section.heading}>
            <h2 className="eyebrow text-[13px] text-blue-deep">
              {section.heading}
            </h2>
            <SectionBody
              section={section}
              className={
                i === 0
                  ? `mt-4 text-[19px] leading-relaxed text-ink md:text-[21px] ${prose}`
                  : `mt-4 text-[17px] leading-relaxed text-ink ${prose}`
              }
            />
          </div>
        ))}
      </div>
      {facts.length > 0 && (
        <aside
          aria-label={`More about ${name}`}
          className="flex flex-col gap-4 lg:sticky lg:top-28"
        >
          {facts.map((section) => (
            <div
              key={section.heading}
              className="rounded-b-brand border-t-4 border-yellow bg-surface px-6 pt-5 pb-6"
            >
              <h2 className="font-display text-[20px] text-slate uppercase">
                {section.heading}
              </h2>
              <SectionBody
                section={section}
                className={`mt-2 text-[15.5px] leading-relaxed text-muted ${prose}`}
              />
            </div>
          ))}
        </aside>
      )}
    </div>
  );
}

export default function TeamProfilePage({ page, path }) {
  const profile = teamProfile(page, path);
  const others = moreTeam(path);
  return (
    <>
      <ProfileHero profile={profile} />

      <section className="bg-white px-4 py-16 sm:px-6 md:py-20">
        <div className="mx-auto max-w-280">
          <Bio profile={profile} />
        </div>
      </section>

      <section className="bg-surface px-4 py-16 sm:px-6 md:py-20">
        <div className="mx-auto max-w-280">
          <div className="on-light mb-9 flex flex-wrap items-end justify-between gap-x-10 gap-y-4">
            <div>
              <span className="eyebrow text-blue-deep">The people</span>
              <h2 className="font-display mt-4 text-[32px] text-slate uppercase md:text-[42px]">
                <Accent>Meet more of *the team.*</Accent>
              </h2>
            </div>
            <TextLink
              href={routes.about + "#team"}
              className="text-blue-deep hover:text-ink"
            >
              See everyone
            </TextLink>
          </div>
          <ul className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {others.map((person) => (
              <li key={person.path}>
                <PersonCard person={person} />
              </li>
            ))}
          </ul>
        </div>
      </section>

      <CtaPanel className="bg-white" title="Put this team *on your problem.*" />
    </>
  );
}
